/**
 * Global tracker for a Client Pack build.
 *
 * Generation is a backend pipeline job: POST /generate returns at once and
 * the build reports per-slide progress on GET /sessions/{id}/progress. This
 * composable polls that, feeds the filmstrip (ClientPackProgress, mounted in
 * the dashboard layout), and when the build ends adds a notification whose
 * toast carries the action — Download on success, Try again on failure.
 *
 * Nothing downloads automatically: once the modal is closed the reader is
 * somewhere else, and a timer-triggered download is both surprising and
 * blockable by the browser.
 *
 * State survives a page reload through resume(): the layout calls it on
 * mount and it picks up the user's in-flight build from the server.
 */
import pitchPackService from '@/api/services/pitchPack'
import { useNotifications } from '@/composables/useNotifications'

const POLL_MS = 1500

// Module-level so every call site drives the same interval.
let pollTimer = null
let polling = false

export const useClientPackJob = () => {
  // { sessionId, companyName, progress, panelOpen }
  const activeJob = useState('client-pack-job', () => null)
  const { addNotification } = useNotifications()

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  const triggerDownload = async (sessionId) => {
    const result = await pitchPackService.download(sessionId)
    if (result.success && result.blob) {
      const url = URL.createObjectURL(result.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = result.filename || `client_pack_${sessionId}.pptx`
      a.click()
      URL.revokeObjectURL(url)
    }
    return result.success
  }

  const skippedLine = (progress) => {
    const skipped = (progress.slides || []).filter(s => s.state === 'suppressed').map(s => s.title)
    const failed = (progress.slides || []).filter(s => s.state === 'failed').map(s => s.title)
    const parts = []
    if (skipped.length) parts.push(`${skipped.length} skipped (${skipped.join(', ')}: no data)`)
    if (failed.length) parts.push(`${failed.length} failed (${failed.join(', ')})`)
    return parts.length ? `, ${parts.join(', ')}` : ''
  }

  const finish = (job, progress) => {
    stopPolling()
    const name = progress.company_name || job.companyName || 'Your client'
    // Watching the filmstrip already shows the outcome and its button, so
    // the toast is only raised when the panel is not open.
    const toast = !job.panelOpen
    if (progress.status === 'completed') {
      addNotification(
        'Client pack ready',
        `${name} · ${progress.built} slides${skippedLine(progress)}`,
        null,
        'client_pack',
        { toast, persistent: true, action: { label: 'Download', kind: 'client_pack_download', sessionId: job.sessionId } },
      )
    } else {
      const where = { assemble: 'while gathering data', slides: 'while building slides', checks: 'during checks', saving: 'while saving' }[progress.failed_phase]
      addNotification(
        'Client pack failed',
        `${name}${where ? ` · stopped ${where}` : ''}`,
        null,
        'client_pack',
        { toast, persistent: true, tone: 'critical', action: { label: 'Try again', kind: 'client_pack_retry', sessionId: job.sessionId } },
      )
    }
    // Keep the finished job while the panel is open so it can show the
    // result; otherwise the pill has nothing left to say.
    activeJob.value = job.panelOpen ? { ...job, progress } : null
  }

  const pollOnce = async () => {
    const job = activeJob.value
    if (!job) { stopPolling(); return }
    if (polling) return
    polling = true
    try {
      const res = await pitchPackService.getProgress(job.sessionId)
      const current = activeJob.value
      if (!current || current.sessionId !== job.sessionId) return
      if (!res.success) {
        // A session that no longer exists or is not ours stops the tracker;
        // anything else is a blip and the next poll retries.
        if (res.status === 404 || res.status === 403) {
          stopPolling()
          activeJob.value = null
        }
        return
      }
      const progress = res.progress
      activeJob.value = { ...current, progress, companyName: progress.company_name || current.companyName }
      if (progress.status === 'completed' || progress.status === 'failed') {
        finish(activeJob.value, progress)
      }
    } finally {
      polling = false
    }
  }

  const track = (sessionId, companyName, { panelOpen }) => {
    stopPolling()
    activeJob.value = { sessionId, companyName, progress: null, panelOpen }
    pollOnce()
    pollTimer = setInterval(pollOnce, POLL_MS)
  }

  /**
   * Queue the build and start tracking it. Returns { success, error }.
   * The panel opens on the filmstrip; the caller closes its own modal.
   */
  const startJob = async (sessionId, companyName, { panelOpen = true } = {}) => {
    const result = await pitchPackService.generate(sessionId)
    if (!result.success) {
      addNotification(
        'Client pack could not start',
        `${companyName || 'Your client'} · ${result.error || 'the build was not queued'}`,
        null,
        'client_pack',
        { persistent: true, tone: 'critical' },
      )
      return { success: false, error: result.error }
    }
    track(sessionId, companyName, { panelOpen })
    return { success: true }
  }

  /** Pick up the user's in-flight build after a reload. */
  const resume = async () => {
    if (activeJob.value) {
      if (!pollTimer && !['completed', 'failed'].includes(activeJob.value.progress?.status)) {
        pollTimer = setInterval(pollOnce, POLL_MS)
      }
      return
    }
    const res = await pitchPackService.listSessions({ status: 'generating', limit: 1 })
    const session = res.success ? res.items[0] : null
    if (session && !activeJob.value) track(session.id, null, { panelOpen: false })
  }

  const openPanel = () => {
    if (activeJob.value) activeJob.value = { ...activeJob.value, panelOpen: true }
  }

  /** Minimise to the pill while building; clear once the build has ended. */
  const closePanel = () => {
    const job = activeJob.value
    if (!job) return
    const ended = ['completed', 'failed'].includes(job.progress?.status)
    activeJob.value = ended ? null : { ...job, panelOpen: false }
  }

  const retry = async (sessionId) => {
    const name = activeJob.value?.companyName
    return startJob(sessionId, name, { panelOpen: true })
  }

  return {
    activeJob: readonly(activeJob),
    startJob,
    resume,
    retry,
    openPanel,
    closePanel,
    triggerDownload,
  }
}
